import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  Button,
  Link
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { 
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface TenantUser {
  id: string;
  name: string;
  email: string;
  tenant_id: string;
  tenant_name: string;
  tenant_subdomain: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  firebase_uid: string;
}

function TenantUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleViewUser = (userId: string) => {
    navigate(`/tenant-users/${userId}`);
  };

  const handleViewTenant = (tenantId: string) => {
    navigate(`/tenants/${tenantId}`);
  };

  const columns: GridColDef[] = [
    { 
      field: 'name', 
      headerName: '名前', 
      width: 150,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          onClick={() => handleViewUser(params.row.id)}
          sx={{ textAlign: 'left', textDecoration: 'none' }}
        >
          {params.value}
        </Link>
      )
    },
    { field: 'email', headerName: 'メールアドレス', width: 250 },
    { 
      field: 'tenant_name', 
      headerName: 'テナント', 
      width: 200,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          onClick={() => handleViewTenant(params.row.tenant_id)}
          sx={{ textAlign: 'left', textDecoration: 'none' }}
        >
          {params.value}
        </Link>
      )
    },
    {
      field: 'role',
      headerName: 'ロール',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={
            params.value === 'admin'
              ? '管理者'
              : params.value === 'user'
              ? 'ユーザー'
              : params.value === 'manager'
              ? 'マネージャー'
              : params.value === 'viewer'
              ? '閲覧者'
              : params.value
          }
          size="small"
          color={params.value === 'admin' ? 'primary' : 'default'}
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'ステータス',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'アクティブ' : '非アクティブ'}
          size="small"
          color={params.value ? 'success' : 'error'}
        />
      ),
    },
    {
      field: 'last_login_at',
      headerName: '最終ログイン',
      width: 150,
      valueFormatter: (params) =>
        params.value
          ? new Date(params.value).toLocaleDateString('ja-JP')
          : '未ログイン',
    },
    {
      field: 'created_at',
      headerName: '作成日',
      width: 150,
      valueFormatter: (params) =>
        new Date(params.value).toLocaleDateString('ja-JP'),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'アクション',
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="詳細表示"
          onClick={() => handleViewUser(params.row.id)}
        />,
        <GridActionsCellItem
          icon={<BusinessIcon />}
          label="テナント詳細"
          onClick={() => handleViewTenant(params.row.tenant_id)}
        />,
      ],
    },
  ];

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 実際のテナントユーザーを取得
      const response = await apiService.getTenantUsers({ 
        limit: 1000,
        search: searchText || undefined
      }) as any;

      if (response.success && response.data) {
        const transformedUsers = response.data.map((user: any) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          tenant_id: user.tenant_id || '',
          tenant_name: user.tenant_name || 'Unknown Tenant',
          tenant_subdomain: user.tenant_subdomain || '',
          role: user.role,
          is_active: user.is_active,
          last_login_at: user.last_login_at,
          created_at: user.created_at,
          firebase_uid: user.firebase_uid || 'N/A'
        }));

        setUsers(transformedUsers);
        
        if (transformedUsers.length === 0) {
          setError('テナントユーザーが登録されていません。');
        }
      } else {
        setUsers([]);
        setError('テナントユーザーの取得に失敗しました。');
      }
      
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      setError(err.message || 'テナントユーザー情報の取得に失敗しました');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // 検索テキストが変更されたときにも再取得
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchUsers();
    }, 500); // 500ms のデバウンス

    return () => clearTimeout(delayedSearch);
  }, [searchText]);

  // 検索はAPIレベルで実装済みのため、フィルタリング不要

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        テナントユーザー管理
      </Typography>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        各テナントに所属するユーザーの管理を行います
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="名前、メールアドレス、テナント名で検索"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={users}
          columns={columns}
          loading={loading}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Paper>
    </Box>
  );
}

export default TenantUsersPage;